import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ArrowLeft, Calendar, Euro, Building2, FileText, 
  CheckCircle2, Trash2, AlertCircle, ChevronDown, Loader2
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useState, useEffect } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DCEUploadAnalyzer, AnalysisResultDisplay, type AnalysisResult, type KeyInfo } from "@/components/tenders/DCEUploadAnalyzer";
import { TenderChecklist } from "@/components/tenders/TenderChecklist";
import { TenderChatPanel } from "@/components/tenders/TenderChatPanel";

type TenderStatus = 
  | "draft" 
  | "analyzing" 
  | "go" 
  | "no_go" 
  | "in_progress" 
  | "submitted" 
  | "won" 
  | "lost" 
  | "cancelled";

interface Tender {
  id: string;
  title: string;
  buyer_name: string | null;
  deadline: string | null;
  budget: number | null;
  status: TenderStatus;
  summary: string | null;
  dce_file_path: string | null;
  dce_document_id: string | null;
  analysis_criteria: Array<{ name: string; weight: number | null }> | null;
  key_info: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

interface TenderTask {
  id: string;
  tender_id: string;
  label: string;
  is_completed: boolean;
  linked_document_id: string | null;
  sort_order: number;
  category: string | null;
  is_mandatory: boolean;
  created_at: string;
}

const STATUS_CONFIG: Record<TenderStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
  draft: { label: "Brouillon", variant: "secondary" },
  analyzing: { label: "Analyse IA", variant: "outline", className: "border-amber-500 text-amber-600 bg-amber-50" },
  go: { label: "Go", variant: "default", className: "bg-emerald-500 hover:bg-emerald-600" },
  no_go: { label: "No Go", variant: "destructive" },
  in_progress: { label: "En cours", variant: "default", className: "bg-blue-500 hover:bg-blue-600" },
  submitted: { label: "Soumis", variant: "outline", className: "border-purple-500 text-purple-600 bg-purple-50" },
  won: { label: "Gagné 🎉", variant: "default", className: "bg-emerald-600 hover:bg-emerald-700" },
  lost: { label: "Perdu", variant: "secondary", className: "bg-muted text-muted-foreground" },
  cancelled: { label: "Annulé", variant: "secondary" },
};

// Status workflow order for manual progression
const STATUS_WORKFLOW: TenderStatus[] = [
  "draft",
  "go",
  "in_progress", 
  "submitted",
  "won",
  "lost",
];

const STATUS_WORKFLOW_LABELS: Record<TenderStatus, string> = {
  draft: "📝 Brouillon",
  analyzing: "🔍 Analyse IA",
  go: "✅ Go",
  no_go: "❌ No Go",
  in_progress: "🚧 En cours",
  submitted: "📤 Soumis",
  won: "🎉 Gagné",
  lost: "😔 Perdu",
  cancelled: "🚫 Annulé",
};

export default function TenderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, status, isHydrated } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);

  // Wait for auth to be hydrated
  if (status === "loading" || status === "hydrating" || !isHydrated) {
    return (
      <div className="container max-w-4xl mx-auto p-4 md:p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Fetch tender details
  const { data: tender, isLoading: tenderLoading, error: tenderError } = useQuery({
    queryKey: ["tender", id],
    queryFn: async () => {
      if (!id) throw new Error("No tender ID");
      const { data, error } = await supabase
        .from("tenders")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      
      // Cast analysis_criteria and key_info from Json to proper types
      return {
        ...data,
        analysis_criteria: (data.analysis_criteria || []) as Array<{ name: string; weight: number | null }>,
        key_info: data.key_info as Record<string, any> | null,
      } as Tender;
    },
    enabled: !!user && !!id,
  });

  // Fetch tender tasks
  const { data: tasks, isLoading: tasksLoading } = useQuery({
    queryKey: ["tender-tasks", id],
    queryFn: async () => {
      if (!id) return [];
      const { data, error } = await supabase
        .from("tender_tasks")
        .select("*")
        .eq("tender_id", id)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as TenderTask[];
    },
    enabled: !!user && !!id,
  });

  // Reconstruct analysis result from DB on mount
  useEffect(() => {
    if (tender && tasks && !analysisResult) {
      // Reconstruct requirements from all tasks (include all categories)
      const requirements = tasks.map(t => ({
        label: t.label,
        category: (t.category || "administrative") as "administrative" | "technical" | "financial" | "reference",
        mandatory: t.is_mandatory,
      }));

      // Get criteria from tender
      const criteria = (tender.analysis_criteria || []) as Array<{ name: string; weight: number | null }>;

      // Reconstruct key_info from tender
      const key_info = tender.key_info as KeyInfo | undefined;

      // Set if we have any analysis data
      if (criteria.length > 0 || tender.summary || key_info) {
        setAnalysisResult({
          summary: tender.summary || "",
          deadline: tender.deadline,
          budget: tender.budget,
          buyer_name: tender.buyer_name,
          requirements,
          criteria,
          key_info,
        });
      }
    }
  }, [tender, tasks]);

  // Update tender status mutation
  const updateStatus = useMutation({
    mutationFn: async (newStatus: TenderStatus) => {
      if (!id) throw new Error("No tender ID");
      const { error } = await supabase
        .from("tenders")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;
      return newStatus;
    },
    onSuccess: (newStatus) => {
      queryClient.invalidateQueries({ queryKey: ["tender", id] });
      queryClient.invalidateQueries({ queryKey: ["tenders"] });
      toast.success(`Statut mis à jour : ${STATUS_WORKFLOW_LABELS[newStatus]}`);
    },
    onError: () => {
      toast.error("Erreur lors de la mise à jour du statut");
    },
  });

  // Delete tender mutation
  const deleteTender = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No tender ID");
      
      // Delete DCE file from storage if exists
      if (tender?.dce_file_path) {
        await supabase.storage.from("tender-dce").remove([tender.dce_file_path]);
      }
      
      const { error } = await supabase
        .from("tenders")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenders"] });
      toast.success("Projet supprimé");
      navigate("/tenders");
    },
    onError: () => {
      toast.error("Erreur lors de la suppression du projet");
    },
  });

  // Update tender with analysis results
  const updateTenderFromAnalysis = useMutation({
    mutationFn: async ({ analysis, dcePath, dceDocumentId }: { analysis: AnalysisResult; dcePath: string; dceDocumentId: string }) => {
      if (!id) throw new Error("No tender ID");
      
      // Update tender fields including DCE path, document ID, criteria, and key_info
      const updates: Record<string, any> = {
        dce_file_path: dcePath,
        dce_document_id: dceDocumentId,
        analysis_criteria: analysis.criteria || [],
        key_info: analysis.key_info || null,
        status: "analyzing",
      };
      if (analysis.summary) updates.summary = analysis.summary;
      if (analysis.deadline) updates.deadline = analysis.deadline;
      if (analysis.budget) updates.budget = analysis.budget;
      if (analysis.buyer_name) updates.buyer_name = analysis.buyer_name;

      const { error: tenderError } = await supabase
        .from("tenders")
        .update(updates)
        .eq("id", id);
      if (tenderError) throw tenderError;

      // Add tasks from requirements with category and mandatory flag
      if (analysis.requirements && analysis.requirements.length > 0) {
        const existingLabels = new Set((tasks || []).map(t => t.label.toLowerCase()));
        const newTasks = analysis.requirements
          .filter(req => !existingLabels.has(req.label.toLowerCase()))
          .map((req, i) => ({
            tender_id: id,
            label: req.label,
            sort_order: (tasks?.length || 0) + i,
            category: req.category,
            is_mandatory: req.mandatory,
          }));

        if (newTasks.length > 0) {
          const { error: tasksError } = await supabase
            .from("tender_tasks")
            .insert(newTasks);
          if (tasksError) throw tasksError;
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tender", id] });
      queryClient.invalidateQueries({ queryKey: ["tender-tasks", id] });
      toast.success("Projet mis à jour avec l'analyse IA");
    },
    onError: (error) => {
      console.error("Update from analysis error:", error);
      toast.error("Erreur lors de la mise à jour");
    },
  });

  const handleAnalysisComplete = (result: AnalysisResult, dcePath: string, dceDocumentId: string) => {
    setAnalysisResult(result);
    updateTenderFromAnalysis.mutate({ analysis: result, dcePath, dceDocumentId });
  };

  if (tenderError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-6">
        <AlertCircle className="w-12 h-12 text-destructive" />
        <p className="text-muted-foreground">Projet introuvable</p>
        <Button variant="outline" onClick={() => navigate("/tenders")}>
          Retour à la liste
        </Button>
      </div>
    );
  }

  if (tenderLoading) {
    return (
      <div className="container max-w-4xl mx-auto p-4 md:p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!tender || !user) return null;

  const statusConfig = STATUS_CONFIG[tender.status];

  return (
    <div className="container max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          className="shrink-0 mt-1"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold">{tender.title}</h1>
            
            {/* Status Selector Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={cn(
                    "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer hover:opacity-90",
                    statusConfig.variant === "secondary" && "bg-secondary text-secondary-foreground",
                    statusConfig.variant === "destructive" && "bg-destructive text-destructive-foreground",
                    statusConfig.variant === "outline" && "border",
                    statusConfig.variant === "default" && "bg-primary text-primary-foreground",
                    statusConfig.className
                  )}
                >
                  {statusConfig.label}
                  <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[160px]">
                {STATUS_WORKFLOW.map((workflowStatus) => {
                  const config = STATUS_CONFIG[workflowStatus];
                  const isCurrent = tender.status === workflowStatus;
                  return (
                    <DropdownMenuItem
                      key={workflowStatus}
                      onClick={() => !isCurrent && updateStatus.mutate(workflowStatus)}
                      className={cn(
                        "cursor-pointer",
                        isCurrent && "bg-accent font-medium"
                      )}
                      disabled={updateStatus.isPending}
                    >
                      <span className="mr-2">{STATUS_WORKFLOW_LABELS[workflowStatus].split(" ")[0]}</span>
                      {config.label}
                      {isCurrent && <CheckCircle2 className="w-4 h-4 ml-auto text-primary" />}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => updateStatus.mutate("no_go")}
                  className="text-destructive cursor-pointer"
                  disabled={updateStatus.isPending}
                >
                  <span className="mr-2">❌</span>
                  No Go
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => updateStatus.mutate("cancelled")}
                  className="text-muted-foreground cursor-pointer"
                  disabled={updateStatus.isPending}
                >
                  <span className="mr-2">🚫</span>
                  Annulé
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
            {tender.buyer_name && (
              <span className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                {tender.buyer_name}
              </span>
            )}
            
            {tender.deadline && (
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {format(new Date(tender.deadline), "d MMMM yyyy", { locale: fr })}
              </span>
            )}

            {tender.budget && (
              <span className="flex items-center gap-1.5">
                <Euro className="w-4 h-4" />
                {new Intl.NumberFormat("fr-FR", { 
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 0 
                }).format(tender.budget)}
              </span>
            )}
          </div>
        </div>

        <Button 
          variant="ghost" 
          size="icon"
          className="text-destructive hover:text-destructive shrink-0"
          onClick={() => setShowDeleteDialog(true)}
        >
          <Trash2 className="w-5 h-5" />
        </Button>
      </div>

      {/* Summary */}
      {tender.summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Résumé
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{tender.summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Checklist */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Checklist
          </CardTitle>
        </CardHeader>
        <CardContent>
          <TenderChecklist
            tenderId={tender.id}
            tasks={tasks}
            isLoading={tasksLoading}
          />
        </CardContent>
      </Card>

      {/* DCE Upload Analyzer */}
      <DCEUploadAnalyzer
        tenderId={tender.id}
        tenderTitle={tender.title}
        userId={user.id}
        existingDcePath={tender.dce_file_path}
        existingDceDocumentId={tender.dce_document_id}
        onAnalysisComplete={handleAnalysisComplete}
      />

      {/* Full Analysis Result Display */}
      {analysisResult && (
        <AnalysisResultDisplay result={analysisResult} />
      )}

      {/* Placeholder for future features */}
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <FileText className="w-6 h-6 text-muted-foreground" />
          </div>
          <h3 className="font-medium text-muted-foreground">Documents liés</h3>
          <p className="text-sm text-muted-foreground/70 mt-1">
            Bientôt : liez vos documents existants à ce projet
          </p>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le projet "{tender.title}" et toutes ses tâches seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTender.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Chat Panel */}
      <TenderChatPanel tenderId={tender.id} tenderTitle={tender.title} />
    </div>
  );
}
